import React, { useState } from 'react';
import { useLists } from '../../context/ListsContext';
import './AddToListModal.css';

const AddToListModal = ({ targetUsername, onClose }) => {
  const { lists, createList, toggleListMember, isInList } = useLists();
  const [newListName, setNewListName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [pendingToggles, setPendingToggles] = useState({});

  const handleToggle = async (listId) => {
    if (pendingToggles[listId]) return;
    setPendingToggles(prev => ({ ...prev, [listId]: true }));
    try {
      await toggleListMember(listId, targetUsername);
    } catch (err) {
      console.error('Toggle list member failed:', err.message);
    } finally {
      setPendingToggles(prev => ({ ...prev, [listId]: false }));
    }
  };

  const handleCreate = async () => {
    const name = newListName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError('');
    try {
      const list = await createList(name);
      setNewListName('');
      // Auto-add the target user to the newly created list
      await toggleListMember(list.id, targetUsername);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create list');
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleCreate();
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="add-to-list-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add @{targetUsername} to a list</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="add-to-list-body">
          {lists.length === 0 && (
            <p className="no-lists-hint">No lists yet. Create one below.</p>
          )}

          <ul className="list-items">
            {lists.map(list => {
              const isMember = isInList(list.id, targetUsername);
              const pending = !!pendingToggles[list.id];
              return (
                <li key={list.id} className="list-item">
                  <button
                    className={`list-toggle-btn ${isMember ? 'is-member' : ''}`}
                    onClick={() => handleToggle(list.id)}
                    disabled={pending}
                  >
                    <span className="list-check">{isMember ? '✓' : ''}</span>
                    <span className="list-name">{list.name}</span>
                    <span className="list-count">{list.members.length}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="create-list-row">
            <input
              className="create-list-input"
              type="text"
              placeholder="New list name…"
              value={newListName}
              onChange={e => setNewListName(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={100}
            />
            <button
              className="create-list-btn"
              onClick={handleCreate}
              disabled={creating || !newListName.trim()}
            >
              {creating ? '…' : 'Create'}
            </button>
          </div>
          {createError && <p className="create-list-error">{createError}</p>}
        </div>
      </div>
    </div>
  );
};

export default AddToListModal;
